import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Galleria } from 'primereact/galleria';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import './FlickrGallery.scss';

const FlickrGallery = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const albumIdFromUrl = searchParams.get('album');
  const [albums, setAlbums] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [loadingAlbums, setLoadingAlbums] = useState(true);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const fetchAlbums = async () => {
      try {
        setLoadingAlbums(true);
        setError(null);

        const response = await fetch('/api/flickr/albums');
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Unable to load photo albums');
        }

        setAlbums(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoadingAlbums(false);
      }
    };

    fetchAlbums();
  }, []);

  useEffect(() => {
    if (!albumIdFromUrl || loadingAlbums || selectedAlbum) return;

    const album = albums.find((entry) => entry.id === albumIdFromUrl);
    if (album) {
      loadAlbumPhotos(album);
    }
  }, [albumIdFromUrl, albums, loadingAlbums, selectedAlbum]);

  const loadAlbumPhotos = async (album, { updateUrl = false } = {}) => {
    try {
      setLoadingPhotos(true);
      setError(null);
      setSelectedAlbum(album);
      setActiveIndex(0);

      if (updateUrl && searchParams.get('album') !== album.id) {
        setSearchParams({ album: album.id });
      }

      const response = await fetch(`/api/flickr/albums/${album.id}/photos`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Unable to load album photos');
      }

      setPhotos(data);
    } catch (err) {
      setError(err.message);
      setSelectedAlbum(null);
      if (searchParams.get('album')) {
        setSearchParams({}, { replace: true });
      }
    } finally {
      setLoadingPhotos(false);
    }
  };

  const openAlbum = (album) => loadAlbumPhotos(album, { updateUrl: true });

  const closeAlbum = () => {
    setSelectedAlbum(null);
    setPhotos([]);
    setActiveIndex(0);
    setSearchParams({}, { replace: true });
  };

  const itemTemplate = (item) => (
    <img
      src={item.src}
      alt={item.title || 'Flickr photo'}
      className="flickr-gallery__photo"
    />
  );

  const thumbnailTemplate = (item) => (
    <img
      src={item.thumbnail}
      alt={item.title || 'Flickr photo thumbnail'}
      className="flickr-gallery__thumbnail"
    />
  );

  if (loadingAlbums) {
    return (
      <div className="flickr-gallery__loading">
        <ProgressSpinner aria-label="Loading photo albums" />
      </div>
    );
  }

  if (error && !selectedAlbum) {
    return <p className="flickr-gallery__error">{error}</p>;
  }

  if (selectedAlbum) {
    return (
      <div className="flickr-gallery__album-view">
        <div className="flickr-gallery__album-header">
          <Button
            label="Back to Albums"
            className="p-button-text"
            onClick={closeAlbum}
          />
          <h2 className="flickr-gallery__album-title">{selectedAlbum.title}</h2>
        </div>

        {loadingPhotos ? (
          <div className="flickr-gallery__loading">
            <ProgressSpinner aria-label="Loading album photos" />
          </div>
        ) : error ? (
          <p className="flickr-gallery__error">{error}</p>
        ) : photos.length === 0 ? (
          <p className="flickr-gallery__empty">This album has no photos yet.</p>
        ) : (
          <Galleria
            value={photos}
            activeIndex={activeIndex}
            onItemChange={(e) => setActiveIndex(e.index)}
            numVisible={7}
            circular
            showItemNavigators
            showThumbnails
            item={itemTemplate}
            thumbnail={thumbnailTemplate}
            className="flickr-gallery__galleria"
          />
        )}
      </div>
    );
  }

  if (albums.length === 0) {
    return <p className="flickr-gallery__empty">No photo albums found.</p>;
  }

  return (
    <div className="flickr-gallery">
      <div className="row g-4">
        {albums.map((album) => (
          <div key={album.id} className="col-12 col-sm-6 col-lg-4">
            <button
              type="button"
              className="flickr-gallery__album-card"
              onClick={() => openAlbum(album)}
            >
              <div className="flickr-gallery__album-cover">
                {album.cover_url ? (
                  <img src={album.cover_url} alt={album.title} />
                ) : (
                  <div className="flickr-gallery__album-placeholder">No cover</div>
                )}
              </div>
              <div className="flickr-gallery__album-meta">
                <h3>{album.title}</h3>
                <p>{album.photo_count} photo{album.photo_count === 1 ? '' : 's'}</p>
              </div>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FlickrGallery;
